import React, { useEffect, useId, useRef, useState } from 'react';
import cx from 'classnames';
import { Alert, Button, IconButton, Tooltip } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import PushPin from '@mui/icons-material/PushPin';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
import ThumbDownAltOutlined from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbDownAlt from '@mui/icons-material/ThumbDownAlt';
import ThumbUpAltOutlined from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpAlt from '@mui/icons-material/ThumbUpAlt';

import AlphaMarkdown from 'components/Alpha/AlphaMarkdown';
import AlphaPendingCard, { IPendingCardActions } from 'components/Alpha/AlphaPendingCard';
import AlphaPodCardScope from 'components/Alpha/AlphaPodCardScope';
import { AlphaVisual } from 'components/Alpha/cards/AlphaVisual';
import { CARD_TITLE_SELECTOR } from 'components/Alpha/cards/cardFocusGuard';
import { CardServerError, CardSubmitResult } from 'components/Alpha/cards/cardTypes';
import { LiveAssistCardRequired } from 'components/Alpha/LiveAssist/liveAssistContract';
import {
  cancelLiveAssistCard,
  cancelLiveAssistCardBeforeDismiss,
  confirmLiveAssistCard,
  loadLiveAssistCardOptions,
  submitLiveAssistCard,
} from 'components/Alpha/LiveAssist/liveAssistCardActions';
import {
  LiveAssistCardView,
  LiveAssistRating,
} from 'components/Alpha/LiveAssist/liveAssistReducer';
import LiveAssistContextDetails from 'components/Alpha/LiveAssist/LiveAssistContextDetails';
import LiveAssistSources from 'components/Alpha/LiveAssist/LiveAssistSources';
import { useLiveAssistStyles } from 'components/Alpha/LiveAssist/liveAssistStyles';
import { toAlphaCard } from 'services/alpha/pendingConfirmations';
import { IAlphaPendingConfirmation } from 'store/alpha/types';

export const CARD_KIND_LABELS: Record<string, string> = {
  context: 'Call context',
  past_tickets: 'Past tickets',
  docs: 'Documentation',
  action: 'Suggested action',
};

const kindLabel = (kind: string): string => CARD_KIND_LABELS[kind] ?? 'Suggestion';

export const DONE_TEXT = 'Done.';

export const dismissCancelFailedText = (title: string): string =>
  `Couldn't cancel "${title}". Try again.`;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toPending = (required: LiveAssistCardRequired, title: string): IAlphaPendingConfirmation => {
  const card = toAlphaCard(required.card);
  return {
    toolName: required.toolName,
    token: required.token,
    kind: required.kind,
    title,
    preview: isPlainObject(card?.preview) ? card?.preview : {},
    ...(card !== undefined ? { card } : {}),
  };
};

interface Props {
  card: LiveAssistCardView;
  sessionId?: number;
  onDismiss: (card: LiveAssistCardView) => void;
  onTogglePin: (card: LiveAssistCardView) => void;
  onRate: (card: LiveAssistCardView, rating: LiveAssistRating) => void;
  onAsk: (question: string) => void;
  onRetry: () => void;
  askDisabled?: boolean;
}

type Required = LiveAssistCardRequired | null | undefined;

const useReview = (
  card: LiveAssistCardView,
  sessionId: number | undefined,
): {
  required?: LiveAssistCardRequired;
  outcome?: string;
  settled: boolean;
  actions?: IPendingCardActions;
  onConfirm: (token: string) => Promise<boolean>;
  onCancel: (token: string) => Promise<void>;
} => {
  const [override, setOverride] = useState<Required>(undefined);
  const [reply, setReply] = useState<string | undefined>();
  const [errors, setErrors] = useState<Record<string, CardServerError>>({});
  const [submitting, setSubmitting] = useState<string[]>([]);
  const [cancelling, setCancelling] = useState<string[]>([]);
  const [settled, setSettled] = useState(true);
  const serverToken = card.card_required?.token;
  const heldRef = useRef(card.card_required);
  if (card.card_required !== undefined) heldRef.current = card.card_required;

  useEffect(() => {
    if (serverToken !== undefined) setOverride(undefined);
  }, [serverToken]);

  const base = card.card_required ?? (settled ? undefined : heldRef.current);
  const required = override === undefined ? base : override ?? undefined;
  const callbacks = {
    replace: (next: LiveAssistCardRequired) => {
      setOverride(next);
    },
    close: () => {
      setOverride(null);
      setReply(previous => previous ?? '');
    },
    reply: (text: string) => {
      setReply(previous => (previous ?? '') + text);
    },
  };
  const toggle = (list: string[], token: string, on: boolean): string[] =>
    on ? [...list.filter(item => item !== token), token] : list.filter(item => item !== token);

  const actions: IPendingCardActions | undefined =
    sessionId === undefined
      ? undefined
      : {
          cardErrors: errors,
          submittingTokens: submitting,
          cancellingTokens: cancelling,
          submitCard: async (token, values): Promise<CardSubmitResult> => {
            setSubmitting(list => toggle(list, token, true));
            setSettled(false);
            try {
              return await submitLiveAssistCard(sessionId, token, values, callbacks);
            } finally {
              setSubmitting(list => toggle(list, token, false));
              setSettled(true);
            }
          },
          cancelCard: async token => {
            setCancelling(list => toggle(list, token, true));
            try {
              const outcome = await cancelLiveAssistCard(sessionId, token);
              setOverride(null);
              setReply(outcome);
            } finally {
              setCancelling(list => toggle(list, token, false));
            }
          },
          loadCardOptions: async (token, source, query) =>
            await loadLiveAssistCardOptions(sessionId, token, source, query),
        };

  const onConfirm = async (token: string): Promise<boolean> => {
    if (sessionId === undefined) return false;
    setErrors(previous =>
      Object.fromEntries(Object.entries(previous).filter(([key]) => key !== token)),
    );
    setSettled(false);
    const outcome = await confirmLiveAssistCard(sessionId, token, callbacks).finally(() => {
      setSettled(true);
    });
    if (!outcome.ok) {
      setErrors(previous => ({ ...previous, [token]: outcome.error }));
      return false;
    }
    callbacks.close();
    return true;
  };

  const onCancel = async (token: string): Promise<void> => {
    if (actions === undefined) return;
    try {
      await actions.cancelCard(token);
    } catch (error) {
      setErrors(previous => ({ ...previous, [token]: { message: (error as Error).message } }));
    }
  };

  return {
    required,
    outcome: override === null ? reply : undefined,
    settled,
    actions,
    onConfirm,
    onCancel,
  };
};

const LiveAssistCard: React.FC<Props> = ({
  card,
  sessionId,
  onDismiss,
  onTogglePin,
  onRate,
  onAsk,
  onRetry,
  askDisabled = false,
}) => {
  const classes = useLiveAssistStyles();
  const titleId = `la-card-${useId().replace(/:/g, '')}`;
  const reviewId = `${titleId}-review`;
  const reviewRef = useRef<HTMLDivElement | null>(null);
  const articleRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [focusReview, setFocusReview] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [dismissFailed, setDismissFailed] = useState(false);
  const dismissingRef = useRef(false);
  const review = useReview(card, sessionId);
  const required = review.actions === undefined ? undefined : review.required;
  const finished = review.outcome !== undefined;
  const cancelling =
    required !== undefined && review.actions?.cancellingTokens?.includes(required.token) === true;

  useEffect(() => {
    if (!focusReview || !open) return;
    setFocusReview(false);
    reviewRef.current?.querySelector<HTMLElement>(CARD_TITLE_SELECTOR)?.focus();
  }, [focusReview, open]);

  useEffect(() => {
    if (!open || (!finished && required !== undefined)) return;
    setOpen(false);
    const active = document.activeElement;
    if (active === null || active === document.body) articleRef.current?.focus();
  }, [finished, open, required]);

  const dismiss = (): void => {
    if (dismissingRef.current || !review.settled || cancelling) return;
    const token = required?.token;
    if (token === undefined || sessionId === undefined) {
      onDismiss(card);
      return;
    }
    dismissingRef.current = true;
    setDismissing(true);
    setDismissFailed(false);
    const settle = (): void => {
      dismissingRef.current = false;
      setDismissing(false);
    };
    cancelLiveAssistCardBeforeDismiss(sessionId, token).then(
      () => {
        settle();
        onDismiss(card);
      },
      () => {
        settle();
        setDismissFailed(true);
      },
    );
  };

  const toggleReview = (): void => {
    setOpen(value => !value);
    setFocusReview(!open);
  };

  const control = (
    label: string,
    pressed: boolean | undefined,
    icon: React.ReactNode,
    onClick: () => void,
    busy = false,
  ): JSX.Element => (
    <Tooltip title={label}>
      <IconButton
        size="small"
        className={classes.iconButton}
        aria-label={label}
        aria-describedby={titleId}
        aria-pressed={pressed}
        aria-disabled={busy || undefined}
        sx={busy ? { opacity: 0.38, cursor: 'progress' } : undefined}
        onClick={onClick}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <article
      ref={articleRef}
      className={cx(classes.card, card.pinned && classes.cardPinned)}
      aria-labelledby={titleId}
      tabIndex={-1}
      data-la-card={card.id}
    >
      <div className={classes.cardTop}>
        <span className={classes.kind}>
          {card.pinned ? `${kindLabel(card.kind)} · Pinned` : kindLabel(card.kind)}
        </span>
        <div className={classes.controls}>
          {control(
            card.pinned ? 'Unpin' : 'Pin',
            card.pinned,
            card.pinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />,
            () => {
              onTogglePin(card);
            },
          )}
          {control(
            'Helpful',
            card.rating === 'up',
            card.rating === 'up' ? (
              <ThumbUpAlt fontSize="small" />
            ) : (
              <ThumbUpAltOutlined fontSize="small" />
            ),
            () => {
              onRate(card, 'up');
            },
          )}
          {control(
            'Not helpful',
            card.rating === 'down',
            card.rating === 'down' ? (
              <ThumbDownAlt fontSize="small" />
            ) : (
              <ThumbDownAltOutlined fontSize="small" />
            ),
            () => {
              onRate(card, 'down');
            },
          )}
          {control(
            'Dismiss',
            undefined,
            <CloseRounded fontSize="small" />,
            dismiss,
            dismissing || !review.settled || cancelling,
          )}
        </div>
      </div>

      <h3 id={titleId} className={classes.cardTitle}>
        {card.title}
      </h3>

      {dismissFailed && required !== undefined && (
        <Alert
          severity="error"
          className={classes.notice}
          action={
            <Button size="small" className={classes.noticeButton} onClick={dismiss}>
              Try again
            </Button>
          }
        >
          {dismissCancelFailedText(card.title)}
        </Alert>
      )}

      {card.body !== undefined && card.body.trim() !== '' && (
        <div className={classes.cardBody}>
          <AlphaMarkdown text={card.body} />
        </div>
      )}

      {card.details !== undefined && (
        <LiveAssistContextDetails details={card.details} onRetry={onRetry} />
      )}

      {card.visual !== undefined && (
        <AlphaPodCardScope>
          <AlphaVisual visual={card.visual} compact />
        </AlphaPodCardScope>
      )}

      <LiveAssistSources citations={card.citations} />

      {(card.actions.length > 0 || required !== undefined) && (
        <div className={classes.actions}>
          {required !== undefined && (
            <Button
              size="small"
              variant={open ? 'outlined' : 'contained'}
              disableElevation
              className={classes.reviewButton}
              aria-expanded={open}
              aria-controls={reviewId}
              onClick={toggleReview}
            >
              {open ? 'Hide' : 'Review'}
            </Button>
          )}
          {card.actions.map(action =>
            action.url !== undefined ? (
              <Button
                key={action.id}
                size="small"
                variant="outlined"
                className={classes.actionButton}
                href={action.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {action.label}
              </Button>
            ) : (
              <Button
                key={action.id}
                size="small"
                variant="outlined"
                className={classes.actionButton}
                disabled={askDisabled}
                onClick={() => {
                  if (action.ask !== undefined) onAsk(action.ask);
                }}
              >
                {action.label}
              </Button>
            ),
          )}
        </div>
      )}

      {required !== undefined && open && (
        <div className={classes.review} id={reviewId} ref={reviewRef}>
          <AlphaPendingCard
            pending={toPending(required, card.title)}
            actions={review.actions}
            onConfirm={review.onConfirm}
            onCancel={review.onCancel}
          />
        </div>
      )}

      {review.outcome === undefined && required === undefined && card.doneLabel !== undefined && (
        <div className={classes.outcome} role="status">
          <span className={classes.outcomeLabel}>Alpha</span>
          <span>{card.doneLabel.trim() === '' ? DONE_TEXT : card.doneLabel}</span>
        </div>
      )}

      {review.outcome !== undefined && (
        <div className={classes.outcome} role="status" aria-busy={!review.settled}>
          <span className={classes.outcomeLabel}>Alpha</span>
          {review.outcome.trim() === '' ? (
            <span>{DONE_TEXT}</span>
          ) : (
            <AlphaMarkdown text={review.outcome} />
          )}
        </div>
      )}
    </article>
  );
};

export default LiveAssistCard;
